import { CompanyType } from '../../../__generated__/graphql'
import dayjs from 'dayjs'

const sanitize = (value: number) => isNaN(value) ? 0 : value
const roundTo = (value: number, decimals: number) => Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)

export interface DateBreakdownRow {
  Date: string
  [key: string]: any // Allow any additional fields
}

interface ColumnConfig {
  dataIndex: string
  excelTitle: string
  getValue: (item: any, companyType: string) => any
}

// Column configurations that map dataIndex to Excel titles and value extraction
export const TEASER_COLUMN_CONFIG: ColumnConfig[] = [
  { dataIndex: 'name', excelTitle: 'Teaser', getValue: (item) => item.teaserName },
  { dataIndex: 'tdImpressions', excelTitle: 'Impressions', getValue: (item) => item.tdImpressions },
  { dataIndex: 'tdClicks', excelTitle: 'Clicks', getValue: (item) => item.tdClicks },
  { dataIndex: 'reads', excelTitle: 'Reads', getValue: (item) => item.reads },
  { dataIndex: 'avgReadtime', excelTitle: 'Avg Readtime', getValue: (item) => item.avgReadtime },
  { dataIndex: 'clickOuts', excelTitle: 'Clickouts', getValue: (item) => item.clickOuts },
  { dataIndex: 'conversions', excelTitle: 'Conversions', getValue: (item) => item.conversions },
  { dataIndex: 'ctr', excelTitle: 'CTR', getValue: (item) => item.ctr },
  { dataIndex: 'imps_reads', excelTitle: 'Reads to Impressions', getValue: (item) => item.imps_reads },
  { dataIndex: 'cotr', excelTitle: 'Click Out Rate', getValue: (item) => item.cotr },
  { dataIndex: 'publisherRevenue', excelTitle: 'Revenue', getValue: (item) => item.publisherRevenue },
  { dataIndex: 'totalCost', excelTitle: 'Total Cost', getValue: (item) => item.totalCost },
  { dataIndex: 'eCpm', excelTitle: 'eCPM', getValue: (item) => item.eCpm },
  { dataIndex: 'cpa', excelTitle: 'CPA', getValue: (item) => item.cpa },
]

export const WEBSITE_COLUMN_CONFIG: ColumnConfig[] = [
  { dataIndex: 'name', excelTitle: 'Website', getValue: (item) => item.websiteName },
  { dataIndex: 'tdImpressions', excelTitle: 'Impressions', getValue: (item) => item.tdImpressions },
  { dataIndex: 'tdClicks', excelTitle: 'Clicks', getValue: (item) => item.tdClicks },
  { dataIndex: 'reads', excelTitle: 'Reads', getValue: (item) => item.reads },
  { dataIndex: 'avgReadtime', excelTitle: 'Avg Readtime', getValue: (item) => item.avgReadtime },
  { dataIndex: 'clickOuts', excelTitle: 'Clickouts', getValue: (item) => item.clickOuts },
  { dataIndex: 'conversions', excelTitle: 'Conversions', getValue: (item) => item.conversions },
  { dataIndex: 'ctr', excelTitle: 'CTR', getValue: (item) => item.ctr },
  { dataIndex: 'imps_reads', excelTitle: 'Reads to Impressions', getValue: (item) => item.imps_reads },
  { dataIndex: 'cotr', excelTitle: 'Click Out Rate', getValue: (item) => item.cotr },
  { dataIndex: 'publisherRevenue', excelTitle: 'Revenue', getValue: (item) => item.publisherRevenue },
  { dataIndex: 'totalCost', excelTitle: 'Total Cost', getValue: (item) => item.totalCost },
  { dataIndex: 'eCpm', excelTitle: 'eCPM', getValue: (item) => item.eCpm },
  { dataIndex: 'cpa', excelTitle: 'CPA', getValue: (item) => item.cpa },
]

export const CHANNEL_COLUMN_CONFIG: ColumnConfig[] = [
  { dataIndex: 'channel', excelTitle: 'Channel', getValue: (item) => item.channelName },
  { dataIndex: 'reads', excelTitle: 'Reads', getValue: (item) => item.reads },
  { dataIndex: 'avgReadtime', excelTitle: 'Avg Readtime', getValue: (item) => item.avgReadtime },
  { dataIndex: 'clickOuts', excelTitle: 'Clickouts', getValue: (item) => item.clickOuts },
  { dataIndex: 'conversions', excelTitle: 'Conversions', getValue: (item) => item.conversions },
]

interface ProcessedItem {
  date: string
  primaryName: string
  tdImpressions: number
  tdClicks: number
  reads: number
  totalCost: number
  publisherRevenue: number
  totalReadtime: number
  clickOuts: number
  conversions: number
  avgReadtime: number
  ctr: string
  imps_reads: string
  cotr: string
  eCpm?: number
  cpa?: number
  websiteName?: string
  teaserName?: string
  channelName?: string
}

export const fetchGenericDateBreakdown = async (
  cubejsApi: any,
  campaignId: string,
  dateRange: string[],
  filters: any[],
  visibleColumns: string[],
  companyType: string,
  columnConfig: ColumnConfig[],
  primaryDimension: 'teaser' | 'website' | 'channel',
  utmSourceFilter?: (utmSource: string) => boolean
): Promise<DateBreakdownRow[]> => {
  // Build dimensions based on primary type
  const dimensions = [
    "aggregateTdLevel.date",
    "aggregateTdLevel.utmSource",
  ]

  if (primaryDimension === 'teaser') {
    dimensions.push("aggregateTdLevel.trafficDriverId", "aggregateTdLevel.tdHeadline")
  } else if (primaryDimension === 'website') {
    dimensions.push("aggregateTdLevel.websiteId", "aggregateTdLevel.websiteName")
  }
  // For channels, we only need utmSource which is already added

  // Execute Cube query with date granularity
  const resultSet = await cubejsApi.load({
    dimensions,
    measures: [
      "aggregateTdLevel.tdImpressions",
      "aggregateTdLevel.tdClicks",
      "aggregateTdLevel.reads",
      "aggregateTdLevel.totalCost",
      "aggregateTdLevel.publisherRevenue",
      "aggregateTdLevel.total_readtime",
      "aggregateTdLevel.clickouts",
      "aggregateTdLevel.conversions",
    ],
    order: {
      "aggregateTdLevel.date": "asc",
    },
    filters: [
      {
        member: "aggregateTdLevel.campaignId",
        operator: "equals",
        values: [campaignId],
      },
      {
        member: "aggregateTdLevel.websiteId",
        operator: "set",
      },
      {
        member: "aggregateTdLevel.publicationId",
        operator: "set",
      },
      {
        member: "aggregateTdLevel.fraud",
        operator: "notEquals",
        values: ["true"],
      },
      ...filters,
    ],
    timeDimensions: [
      {
        dimension: "aggregateTdLevel.date",
        granularity: "day",
        dateRange: dateRange,
      },
    ],
  })

  const rawData = resultSet.loadResponse.results[0].data

  // Filter data based on utm source if provided
  const filteredData = utmSourceFilter
    ? rawData.filter((item: any) => utmSourceFilter(item['aggregateTdLevel.utmSource']))
    : rawData

  // Map channel names from utm sources
  const mapChannelName = (utmSource: string): string => {
    if (utmSource === '' || utmSource === 'ADSERVER') return 'Teasers'
    if (utmSource === 'ORGANIC') return 'Organic'
    if (utmSource === 'SOCIAL') return 'Social'
    if (utmSource === 'DIRECT') return 'Direct'
    if (utmSource === 'INTERNAL_REFERRAL') return 'Internal Referral'
    if (utmSource === 'EXTERNAL_REFERRAL') return 'External Referral'
    return 'Other'
  }

  // Process and transform data
  const processedItems: ProcessedItem[] = filteredData.map((item: any) => {
    const date = dayjs(item['aggregateTdLevel.date']).format('YYYY-MM-DD')

    // Determine primary name based on type
    let primaryName = ''
    if (primaryDimension === 'teaser') {
      primaryName = item['aggregateTdLevel.tdHeadline'] || 'Unknown Teaser'
    } else if (primaryDimension === 'website') {
      primaryName = item['aggregateTdLevel.websiteName'] || 'Unknown Website'
    } else if (primaryDimension === 'channel') {
      primaryName = mapChannelName(item['aggregateTdLevel.utmSource'])
    }

    // Parse all numeric values
    const tdImpressions = sanitize(parseFloat(item['aggregateTdLevel.tdImpressions']))
    const tdClicks = sanitize(parseFloat(item['aggregateTdLevel.tdClicks']))
    const reads = sanitize(parseFloat(item['aggregateTdLevel.reads']))
    const totalCost = sanitize(parseFloat(item['aggregateTdLevel.totalCost']))
    const publisherRevenue = sanitize(parseFloat(item['aggregateTdLevel.publisherRevenue']))
    const totalReadtime = sanitize(parseFloat(item['aggregateTdLevel.total_readtime']))
    const clickOuts = sanitize(parseFloat(item['aggregateTdLevel.clickouts']))
    const conversions = sanitize(parseFloat(item['aggregateTdLevel.conversions']))

    // Calculate derived metrics
    const avgReadtime = reads > 0 ? roundTo((totalReadtime / 1000) / reads, 1) : 0
    const ctr = tdImpressions > 0 ? roundTo((tdClicks / tdImpressions) * 100, 2) : 0
    const impsReads = tdImpressions > 0 ? roundTo((reads / tdImpressions) * 1000, 2) : 0
    const cotr = reads > 0 ? roundTo((clickOuts / reads) * 100, 2) : 0

    const processedItem: ProcessedItem = {
      date,
      primaryName,
      tdImpressions,
      tdClicks,
      reads,
      totalCost,
      publisherRevenue,
      totalReadtime,
      clickOuts,
      conversions,
      avgReadtime,
      ctr: `${ctr}%`,
      imps_reads: `${impsReads} per 1000`,
      cotr: `${cotr}%`,
    }

    // Add type-specific names for easier column mapping
    if (primaryDimension === 'teaser') {
      processedItem.teaserName = primaryName
    } else if (primaryDimension === 'website') {
      processedItem.websiteName = primaryName
    } else if (primaryDimension === 'channel') {
      processedItem.channelName = primaryName
    }

    // Add company-specific fields
    if (companyType === CompanyType.Publisher) {
      const revenue = roundTo(publisherRevenue, 2)
      const eCpm = tdImpressions > 0 ? roundTo(revenue / (tdImpressions / 1000), 2) : 0
      processedItem.publisherRevenue = revenue
      processedItem.eCpm = eCpm
    } else {
      const cost = roundTo(totalCost, 2)
      const eCpm = tdImpressions > 0 ? roundTo(cost / (tdImpressions / 1000), 2) : 0
      const cpa = conversions > 0 ? roundTo(cost / conversions, 2) : 0
      processedItem.totalCost = cost
      processedItem.eCpm = eCpm
      processedItem.cpa = cpa
    }

    return processedItem
  })

  // Build export rows with only visible columns
  const rows: DateBreakdownRow[] = processedItems.map(item => {
    const row: DateBreakdownRow = {
      Date: item.date,
    }

    // Add columns based on configuration and visibility
    columnConfig.forEach(config => {
      if (visibleColumns.includes(config.dataIndex)) {
        const value = config.getValue(item, companyType)
        if (value !== undefined) {
          row[config.excelTitle] = value
        }
      }
    })

    return row
  })

  return rows
}
